/**
 * RepeatableGroupRenderer — generic renderer for catalog repeatable
 * groups.
 *
 * Wraps the existing UI `RepeatableGroup` composite, supplying its
 * `renderInstance` callback with a list of FieldRenderer mounts and
 * inline AddressComposite mounts for any clustered address sub-fields.
 *
 * Address clustering used to skip repeatable rows (a Phase 7a
 * shortcut), so prior addresses, employment addresses, sponsor address
 * history, and any other repeatable address bag rendered as a vertical
 * stack of standalone fields — different layout and labels from the
 * top-level current/mailing address cards. This renderer now applies
 * the same `getAddressClusterKey` heuristic per row, so a single row's
 * country / street / unit_type+unit_number+city / state+zip collapse
 * into one `AddressComposite` with the same simplified labels and
 * proportional grid as the singleton address paths.
 *
 * Caller responsibilities:
 *   - Pass the catalog's per-group child fields (`fields`).
 *   - Pass the bound, the legend, and the instance-id roster.
 *   - Track the touched set; the renderer wraps each child field-id
 *     with the instance id (`${fieldId}#${instanceId}`).
 */
import { useEffect, useState } from 'react';
import type {
  AnswerValue,
  FieldDefinition,
  FieldId,
} from '@inside-us-aos/domain';
import type { FormEngine } from '@inside-us-aos/form-engine';
import {
  AddressComposite,
  type IAddressFieldEntry,
  type IRepeatableBoundView,
  type IUploadedFileMetadata,
  NameComposite,
  RepeatableGroup,
} from '@inside-us-aos/ui';

import { FieldRenderer } from './FieldRenderer';
import { getAddressClusterKey } from './addressClustering';
import { projectAddressEntriesForCountry } from './addressEntryProjection';
import { resolveFieldOptions } from './optionsResolver';
import { withUserHelpText } from './userHelpText';
import { projectNameTriplet } from './SectionRenderer';

export interface RepeatableGroupRendererProps {
  /** Heading rendered above the instance list. */
  legend: string;
  /** Catalog group id (used for documentation; not rendered). */
  groupId: string;
  /** Child fields of this group, in catalog order. */
  fields: readonly FieldDefinition[];
  /** Instance ids — one per repeatable row. */
  instanceIds: readonly string[];
  /** Catalog's repeatableBound mapped to the UI's bound view. */
  bound: IRepeatableBoundView;
  onAddInstance: () => void;
  onRemoveInstance: (instanceId: string) => void;
  engine: FormEngine;
  touchedFieldIds: ReadonlySet<string>;
  /** Marks a touched key (typically `${fieldId}#${instanceId}`). */
  onTouchKey: (key: string) => void;
  /** Optional label for the Add Row button (defaults to "Add row"). */
  addLabel?: string;
  /**
   * Upload bridge — passed through to FieldRenderer for any
   * document_upload fields nested in the repeatable rows.
   */
  uploadDocument?: (
    fieldId: FieldId,
    file: IUploadedFileMetadata,
  ) => Promise<{ uploadedFileId: string }>;
  onUploadError?: (fieldId: string, message: string) => void;
}

function instanceTouchedKey(fieldId: string, instanceId: string): string {
  return `${fieldId}#${instanceId}`;
}

function readTextOrSelect(
  engine: FormEngine,
  fieldId: FieldId,
  instanceId: string,
): string {
  const v: AnswerValue | undefined = engine.getAnswer(fieldId, instanceId);
  if (!v) return '';
  if (v.type === 'text' || v.type === 'select') return v.value;
  return '';
}

function readDate(
  engine: FormEngine,
  fieldId: FieldId,
  instanceId: string,
): string {
  const v: AnswerValue | undefined = engine.getAnswer(fieldId, instanceId);
  if (!v || v.type !== 'date') return '';
  return v.value;
}

/**
 * Inline render-unit shape used to keep one row's clustered + standalone
 * fields in catalog order. The first member of each address cluster
 * anchors the cluster's position in the row.
 *
 * `name` triplet support added 2026-05-26 — repeatable rows (children,
 * other-names, prior-marriages) carry first/middle/last sub-fields
 * that should render side-by-side under one legend, not stacked as
 * three full-row TextInputs. SectionRenderer already does this for
 * non-repeatable triplets; this renderer parallels it for repeatable
 * rows.
 */
type RowRenderUnit =
  | { kind: 'field'; field: FieldDefinition }
  | { kind: 'address'; legend: string; fields: readonly FieldDefinition[] }
  | {
      kind: 'name';
      legend: string;
      firstNameField: FieldDefinition;
      middleNameField: FieldDefinition;
      lastNameField: FieldDefinition;
    };

function findRepeatableNameTriplet(
  prefix: string,
  byId: ReadonlyMap<string, FieldDefinition>,
): { first: FieldDefinition; middle: FieldDefinition; last: FieldDefinition } | null {
  const first = byId.get(`${prefix}.first`);
  const middle = byId.get(`${prefix}.middle`);
  const last = byId.get(`${prefix}.last`);
  if (
    first === undefined ||
    middle === undefined ||
    last === undefined ||
    first.valueType !== 'text' ||
    middle.valueType !== 'text' ||
    last.valueType !== 'text'
  ) {
    return null;
  }
  return { first, middle, last };
}

function planRowRenderUnits(
  fields: readonly FieldDefinition[],
): readonly RowRenderUnit[] {
  const byId = new Map<string, FieldDefinition>(
    fields.map(f => [f.id as string, f]),
  );
  const consumed = new Set<string>();
  const units: RowRenderUnit[] = [];
  const anchored = new Set<string>();
  for (const field of fields) {
    if (consumed.has(field.id as string)) continue;

    // Name triplet detection — runs BEFORE address clustering so a
    // row that mixes name fields + an address still collapses each
    // into its own composite.
    const idStr = field.id as string;
    let nameTripletPrefix: string | null = null;
    if (field.valueType === 'text') {
      if (idStr.endsWith('.first')) {
        nameTripletPrefix = idStr.slice(0, -'.first'.length);
      } else if (idStr.endsWith('.middle')) {
        nameTripletPrefix = idStr.slice(0, -'.middle'.length);
      } else if (idStr.endsWith('.last')) {
        nameTripletPrefix = idStr.slice(0, -'.last'.length);
      }
    }
    if (nameTripletPrefix !== null) {
      const triplet = findRepeatableNameTriplet(nameTripletPrefix, byId);
      if (triplet !== null) {
        consumed.add(triplet.first.id as string);
        consumed.add(triplet.middle.id as string);
        consumed.add(triplet.last.id as string);
        const projected = projectNameTriplet(
          triplet.first.label,
          triplet.middle.label,
          triplet.last.label,
        );
        const legend = projected?.legend ?? triplet.first.label;
        const firstNameField: FieldDefinition = projected
          ? ({ ...triplet.first, label: projected.firstLabel } as FieldDefinition)
          : triplet.first;
        const middleNameField: FieldDefinition = projected
          ? ({ ...triplet.middle, label: projected.middleLabel } as FieldDefinition)
          : triplet.middle;
        const lastNameField: FieldDefinition = projected
          ? ({ ...triplet.last, label: projected.lastLabel } as FieldDefinition)
          : triplet.last;
        units.push({
          kind: 'name',
          legend,
          firstNameField,
          middleNameField,
          lastNameField,
        });
        continue;
      }
    }

    const clusterKey = getAddressClusterKey(field);
    if (clusterKey === null) {
      consumed.add(field.id as string);
      units.push({ kind: 'field', field });
      continue;
    }
    if (anchored.has(clusterKey)) {
      consumed.add(field.id as string);
      continue;
    }
    const clusterFields = fields.filter(
      f => getAddressClusterKey(f) === clusterKey,
    );
    if (clusterFields.length < 2) {
      consumed.add(field.id as string);
      units.push({ kind: 'field', field });
      continue;
    }
    anchored.add(clusterKey);
    for (const cf of clusterFields) consumed.add(cf.id as string);
    const ordered = [...clusterFields].sort((a, b) => {
      const aIsCountry = (a.id as string).endsWith('.country');
      const bIsCountry = (b.id as string).endsWith('.country');
      if (aIsCountry && !bIsCountry) return -1;
      if (!aIsCountry && bIsCountry) return 1;
      return 0;
    });
    const bagSegments = clusterKey.split('.');
    const bagName = bagSegments[bagSegments.length - 1];
    const legend = bagName
      .split('_')
      .map(p => (p.length === 0 ? p : p[0].toUpperCase() + p.slice(1)))
      .join(' ');
    units.push({ kind: 'address', legend, fields: ordered });
  }
  return units;
}

export function RepeatableGroupRenderer(
  props: RepeatableGroupRendererProps,
): JSX.Element {
  const {
    legend,
    fields,
    instanceIds,
    bound,
    onAddInstance,
    onRemoveInstance,
    engine,
    touchedFieldIds,
    onTouchKey,
    addLabel,
    uploadDocument,
    onUploadError,
  } = props;

  // Subscribe to engine answer changes so address + name composites
  // (which don't subscribe themselves) re-render with fresh values on
  // every keystroke. Added 2026-05-26 — before this, NameComposite
  // rows showed only the first character typed because the parent
  // never re-rendered after engine.setAnswer fired.
  const [, setRevision] = useState(0);
  useEffect(
    () => engine.subscribe(() => setRevision(prev => prev + 1)),
    [engine],
  );

  const rowUnits = planRowRenderUnits(fields);

  return (
    <RepeatableGroup
      legend={legend}
      instanceIds={instanceIds}
      bound={bound}
      onAddInstance={onAddInstance}
      onRemoveInstance={onRemoveInstance}
      addLabel={addLabel}
      renderInstance={instanceId => (
        <div>
          {rowUnits.map(unit => {
            if (unit.kind === 'field') {
              const key = instanceTouchedKey(unit.field.id, instanceId);
              return (
                <FieldRenderer
                  key={`${unit.field.id}-${instanceId}`}
                  field={unit.field}
                  engine={engine}
                  touched={touchedFieldIds.has(key)}
                  onTouch={() => onTouchKey(key)}
                  groupInstanceId={instanceId}
                  uploadDocument={uploadDocument}
                  onUploadError={onUploadError}
                />
              );
            }
            const issuesByFieldId =
              engine.getValidationState().issuesByFieldId;
            if (unit.kind === 'name') {
              const tripletTouched = new Set<string>();
              if (
                touchedFieldIds.has(
                  instanceTouchedKey(unit.firstNameField.id, instanceId),
                )
              ) {
                tripletTouched.add(unit.firstNameField.id as string);
              }
              if (
                touchedFieldIds.has(
                  instanceTouchedKey(unit.middleNameField.id, instanceId),
                )
              ) {
                tripletTouched.add(unit.middleNameField.id as string);
              }
              if (
                touchedFieldIds.has(
                  instanceTouchedKey(unit.lastNameField.id, instanceId),
                )
              ) {
                tripletTouched.add(unit.lastNameField.id as string);
              }
              return (
                <NameComposite
                  key={`${unit.legend}-${instanceId}`}
                  legend={unit.legend}
                  firstNameField={unit.firstNameField}
                  middleNameField={unit.middleNameField}
                  lastNameField={unit.lastNameField}
                  values={{
                    first: readTextOrSelect(
                      engine,
                      unit.firstNameField.id,
                      instanceId,
                    ),
                    middle: readTextOrSelect(
                      engine,
                      unit.middleNameField.id,
                      instanceId,
                    ),
                    last: readTextOrSelect(
                      engine,
                      unit.lastNameField.id,
                      instanceId,
                    ),
                  }}
                  issuesByFieldId={issuesByFieldId}
                  touchedFieldIds={tripletTouched}
                  onChange={(sub, next) => {
                    const target =
                      sub === 'first'
                        ? unit.firstNameField
                        : sub === 'middle'
                        ? unit.middleNameField
                        : unit.lastNameField;
                    engine.setAnswer(
                      target.id,
                      { type: 'text', value: next },
                      instanceId,
                    );
                    onTouchKey(instanceTouchedKey(target.id, instanceId));
                  }}
                  onBlur={sub => {
                    const target =
                      sub === 'first'
                        ? unit.firstNameField
                        : sub === 'middle'
                        ? unit.middleNameField
                        : unit.lastNameField;
                    onTouchKey(instanceTouchedKey(target.id, instanceId));
                  }}
                />
              );
            }
            const rawEntries: IAddressFieldEntry[] = unit.fields.map(field => ({
              field: withUserHelpText(field),
              value:
                field.valueType === 'date'
                  ? readTextOrSelect(engine, field.id, instanceId) ||
                    readDate(engine, field.id, instanceId)
                  : readTextOrSelect(engine, field.id, instanceId),
              issues: issuesByFieldId.get(field.id) ?? [],
              touched: touchedFieldIds.has(
                instanceTouchedKey(field.id, instanceId),
              ),
              selectOptions:
                field.valueType === 'select'
                  ? resolveFieldOptions(field)
                  : undefined,
              onChange: (next: string) => {
                if (field.valueType === 'select') {
                  engine.setAnswer(
                    field.id,
                    { type: 'select', value: next },
                    instanceId,
                  );
                } else if (field.valueType === 'date') {
                  engine.setAnswer(
                    field.id,
                    { type: 'date', value: next },
                    instanceId,
                  );
                } else {
                  engine.setAnswer(
                    field.id,
                    { type: 'text', value: next },
                    instanceId,
                  );
                }
                onTouchKey(instanceTouchedKey(field.id, instanceId));
              },
              onBlur: () =>
                onTouchKey(instanceTouchedKey(field.id, instanceId)),
            }));
            const entries = projectAddressEntriesForCountry(rawEntries);
            return (
              <AddressComposite
                key={`${unit.legend}-${instanceId}`}
                legend={unit.legend}
                entries={entries}
              />
            );
          })}
        </div>
      )}
    />
  );
}
